import { type MouseEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bolt, FactCheck, MoreVert, OpenInNew, TaskAlt, VisibilityOff } from '@mui/icons-material';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { BackButton } from '../components/back-button';
import { CardTitle } from '../components/card-title';
import { ClaimQueueCounts } from '../components/claim-queue-counts';
import {
  useHiddenMorningWorklistOrganizationsQuery,
  useHideMorningWorklistOrganizationMutation,
  useMorningWorklistQuery,
  useUnhideMorningWorklistOrganizationMutation,
} from '../store/hooks';
import type {
  MorningWorklistClaimCounts,
  MorningWorklistOrganization,
  MorningWorklistWorkItem,
} from '../types';
import { adminClaimWorkUrl } from '../utils/claimWorkNavigation';

type Urgency = 'critical' | 'high' | 'medium' | 'watch';
type Owner = 'rcm_ops' | 'payer';

const WORKLIST_PAGE_SIZE = 100;

const urgencyStyles: Record<
  Urgency,
  { chip: 'error' | 'warning' | 'info' | 'success'; border: string }
> = {
  critical: { chip: 'error', border: 'border-rose-300' },
  high: { chip: 'warning', border: 'border-amber-300' },
  medium: { chip: 'info', border: 'border-blue-300' },
  watch: { chip: 'success', border: 'border-emerald-300' },
};

const ownerLabels: Record<Owner, string> = {
  rcm_ops: 'RCM Ops',
  payer: 'Payer',
};

const emptyClaims: MorningWorklistClaimCounts = {
  submit: 0,
  review: 0,
  awaiting_payer: 0,
  fix: 0,
};

function getUrgency(value: MorningWorklistOrganization['urgency']): Urgency {
  return value ?? 'watch';
}

function getOwner(value: MorningWorklistWorkItem['owner']): Owner {
  return value ?? 'rcm_ops';
}

function getWorkItems(row: MorningWorklistOrganization) {
  return row.work_items ?? [];
}

function getVisibleOrgCount(
  rows: MorningWorklistOrganization[],
  urgencyFilter: 'all' | Urgency,
  ownerFilter: 'all' | Owner,
) {
  return rows
    .filter(row => urgencyFilter === 'all' || getUrgency(row.urgency) === urgencyFilter)
    .filter(
      row =>
        ownerFilter === 'all' ||
        getWorkItems(row).some(item => getOwner(item.owner) === ownerFilter),
    ).length;
}

function WorkItemIcon({ owner }: { owner: Owner }) {
  const Icon = owner === 'payer' ? FactCheck : TaskAlt;
  return <Icon fontSize="small" />;
}

export default function MorningWorklist() {
  const navigate = useNavigate();
  const eventTracking = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();
  const [urgencyFilter, setUrgencyFilter] = useState<'all' | Urgency>('all');
  const [ownerFilter, setOwnerFilter] = useState<'all' | Owner>('all');
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuRow, setMenuRow] = useState<MorningWorklistOrganization | null>(null);
  const [hiddenModalOpen, setHiddenModalOpen] = useState(false);

  const { data, error, isFetching, isLoading, refetch } = useMorningWorklistQuery({
    page: 1,
    perPage: WORKLIST_PAGE_SIZE,
    sortBy: 'morning_priority',
    sortDir: 'desc',
  });
  const {
    data: hiddenOrganizationsData,
    isFetching: hiddenOrganizationsFetching,
    refetch: refetchHiddenOrganizations,
  } = useHiddenMorningWorklistOrganizationsQuery(undefined, {
    skip: !hiddenModalOpen,
  });
  const [hideOrganization, { isLoading: isHidingOrganization }] =
    useHideMorningWorklistOrganizationMutation();
  const [unhideOrganization, { isLoading: isUnhidingOrganization }] =
    useUnhideMorningWorklistOrganizationMutation();

  const rows = useMemo(() => data?.organizations ?? [], [data?.organizations]);
  const claims = data?.summary.claims ?? emptyClaims;
  const hiddenOrganizations = hiddenOrganizationsData?.organizations ?? [];

  const filteredRows = useMemo(() => {
    return rows
      .filter(row => urgencyFilter === 'all' || getUrgency(row.urgency) === urgencyFilter)
      .filter(
        row =>
          ownerFilter === 'all' ||
          getWorkItems(row).some(item => getOwner(item.owner) === ownerFilter),
      );
  }, [ownerFilter, rows, urgencyFilter]);

  function trackUrgencyFilterChange(value: 'all' | Urgency) {
    eventTracking.billing.morningWorklistFilterChanged({
      filter_name: 'urgency',
      filter_value: value,
      visible_org_count: getVisibleOrgCount(rows, value, ownerFilter),
    });
    setUrgencyFilter(value);
  }

  function trackOwnerFilterChange(value: 'all' | Owner) {
    eventTracking.billing.morningWorklistFilterChanged({
      filter_name: 'owner',
      filter_value: value,
      visible_org_count: getVisibleOrgCount(rows, urgencyFilter, value),
    });
    setOwnerFilter(value);
  }

  function openClassicDashboard() {
    eventTracking.billing.morningWorklistClassicDashboardClicked({
      visible_org_count: filteredRows.length,
    });
    navigate('/admin-v2/billing-dashboard');
  }

  function openAccountWork(row: MorningWorklistOrganization) {
    eventTracking.billing.morningWorklistAccountClicked({
      organization_id: row.organization_id,
      urgency: getUrgency(row.urgency),
      rank:
        filteredRows.findIndex(filteredRow => filteredRow.organization_id === row.organization_id) +
        1,
      work_item_count: getWorkItems(row).length,
    });
    navigate(adminClaimWorkUrl(row.organization_id, 'all_claim_work'));
  }

  function openRowMenu(event: MouseEvent<HTMLElement>, row: MorningWorklistOrganization) {
    setMenuAnchorEl(event.currentTarget);
    setMenuRow(row);
  }

  function closeRowMenu() {
    setMenuAnchorEl(null);
    setMenuRow(null);
  }

  async function hideSelectedOrganization() {
    if (!menuRow) return;

    const organizationName = menuRow.organization_name ?? 'Organization';
    const organizationId = menuRow.organization_id;
    closeRowMenu();

    try {
      await hideOrganization({
        hideMorningWorklistOrganizationRequest: {
          organization_id: organizationId,
          reason: 'Hidden from RCM morning worklist',
        },
      }).unwrap();
      enqueueSnackbar(`${organizationName} hidden from the morning worklist`, {
        variant: 'success',
      });
      refetch();
      if (hiddenModalOpen) {
        refetchHiddenOrganizations();
      }
    } catch {
      enqueueSnackbar(`Failed to hide ${organizationName}`, { variant: 'error' });
    }
  }

  function openHiddenModal() {
    setHiddenModalOpen(true);
  }

  function closeHiddenModal() {
    setHiddenModalOpen(false);
  }

  async function unhideSelectedOrganization(
    organizationId: string,
    organizationName?: string | null,
  ) {
    try {
      await unhideOrganization({ organizationId }).unwrap();
      enqueueSnackbar(`${organizationName ?? 'Organization'} restored to the morning worklist`, {
        variant: 'success',
      });
      refetchHiddenOrganizations();
      refetch();
    } catch {
      enqueueSnackbar(`Failed to restore ${organizationName ?? 'organization'}`, {
        variant: 'error',
      });
    }
  }

  return (
    <div data-testid="morning-worklist" className="min-h-full min-w-0 bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-8">
        <BackButton fallbackUrl="/admin-v2/billing-dashboard" />

        <div className="mt-4 mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-center gap-3">
            <Bolt className="text-blue-600" fontSize="large" />
            <Typography variant="h4" className="font-bold text-gray-900">
              RCM Morning Worklist
            </Typography>
          </div>
          <Button variant="outlined" endIcon={<OpenInNew />} onClick={openClassicDashboard}>
            Open classic billing dashboard
          </Button>
        </div>

        <Card data-testid="morning-worklist-summary" className="mb-4">
          <CardContent>
            <CardTitle>Claims</CardTitle>
            <ClaimQueueCounts counts={claims} />
          </CardContent>
        </Card>

        <Card className="mb-4">
          <CardContent>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <CardTitle>Accounts</CardTitle>
              <div className="flex flex-col gap-2 md:flex-row">
                <Button
                  variant="outlined"
                  startIcon={<VisibilityOff />}
                  onClick={openHiddenModal}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Hidden orgs
                </Button>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Urgency</InputLabel>
                  <Select
                    label="Urgency"
                    value={urgencyFilter}
                    onChange={event => trackUrgencyFilterChange(event.target.value)}
                  >
                    <MenuItem value="all">All urgency</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="watch">Watch</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Owner</InputLabel>
                  <Select
                    label="Owner"
                    value={ownerFilter}
                    onChange={event => trackOwnerFilterChange(event.target.value)}
                  >
                    <MenuItem value="all">All owners</MenuItem>
                    <MenuItem value="rcm_ops">RCM Ops</MenuItem>
                    <MenuItem value="payer">Payer</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>
          </CardContent>
        </Card>

        {error ? (
          <Alert severity="error" className="mb-4">
            Morning worklist failed to load.
          </Alert>
        ) : null}

        {isLoading ? (
          <Card>
            <CardContent>
              <div className="flex items-center justify-center py-12">
                <CircularProgress size={28} />
              </div>
            </CardContent>
          </Card>
        ) : null}

        {!isLoading && !error && filteredRows.length === 0 ? (
          <Card>
            <CardContent>
              <Typography className="text-gray-600">
                No organizations match the current filters.
              </Typography>
            </CardContent>
          </Card>
        ) : null}

        <div className={isFetching && !isLoading ? 'space-y-3 opacity-70' : 'space-y-3'}>
          {filteredRows.map((row, rowIndex) => {
            const urgency = getUrgency(row.urgency);
            const urgencyStyle = urgencyStyles[urgency];
            const rank = rowIndex + 1;
            const workItems = getWorkItems(row);

            return (
              <Card key={row.organization_id} className={`border ${urgencyStyle.border}`}>
                <CardContent>
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_minmax(0,1fr)_minmax(0,1fr)]">
                    <div>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-semibold text-gray-500">#{String(rank)}</div>
                          <Chip label={urgency} color={urgencyStyle.chip} size="small" />
                        </div>
                        <IconButton
                          aria-label={`Actions for ${row.organization_name ?? 'organization'}`}
                          size="small"
                          onClick={event => openRowMenu(event, row)}
                          disabled={isHidingOrganization}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </div>
                      <Typography variant="h6" className="mt-2 font-bold text-gray-900">
                        {row.organization_name ?? 'Unknown organization'}
                      </Typography>
                      <Button
                        className="mt-4"
                        fullWidth
                        variant="outlined"
                        endIcon={<OpenInNew />}
                        onClick={() => openAccountWork(row)}
                      >
                        Open account work
                      </Button>
                    </div>

                    <div>
                      <div className="text-xs font-medium uppercase text-gray-500">Claims</div>
                      <ClaimQueueCounts
                        counts={row.claims}
                        onSelect={view => navigate(adminClaimWorkUrl(row.organization_id, view))}
                      />
                    </div>

                    <div>
                      <div className="text-xs font-medium uppercase text-gray-500">Work to do</div>
                      <div className="mt-2 space-y-2">
                        {workItems.map(item => {
                          const owner = getOwner(item.owner);
                          const itemUrgency = item.urgency ?? 'watch';

                          return (
                            <div
                              key={`${item.kind}-${owner}`}
                              className="grid grid-cols-[minmax(0,1fr)_56px] items-center gap-2 rounded-md bg-gray-50 p-2 text-sm"
                            >
                              <div className="flex items-center gap-2">
                                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-gray-700">
                                  <WorkItemIcon owner={owner} />
                                </div>
                                <div className="min-w-0">
                                  <div className="font-semibold leading-tight text-gray-900">
                                    {item.label}
                                  </div>
                                  <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-1 text-xs text-gray-500">
                                    <span>{ownerLabels[owner]}</span>
                                    {item.oldest_age_days == null ? null : (
                                      <span>{String(item.oldest_age_days)}d</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <Chip
                                label={String(item.count ?? 0)}
                                color={urgencyStyles[itemUrgency].chip}
                                size="small"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={closeRowMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={hideSelectedOrganization} disabled={isHidingOrganization}>
            Hide from list
          </MenuItem>
        </Menu>
        <Dialog open={hiddenModalOpen} onClose={closeHiddenModal} fullWidth maxWidth="sm">
          <DialogTitle>Hidden organizations</DialogTitle>
          <DialogContent>
            {hiddenOrganizationsFetching ? (
              <div className="flex items-center justify-center py-8">
                <CircularProgress size={24} />
              </div>
            ) : hiddenOrganizations.length === 0 ? (
              <Typography className="py-3 text-gray-600">
                No organizations are hidden from the morning worklist.
              </Typography>
            ) : (
              <div className="space-y-2 pt-1">
                {hiddenOrganizations.map(organization => (
                  <div
                    key={organization.organization_id}
                    className="flex flex-col gap-3 rounded-md border border-gray-200 p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <Typography className="font-semibold text-gray-900">
                        {organization.organization_name ?? 'Unknown organization'}
                      </Typography>
                      <Typography variant="body2" className="text-gray-500">
                        {organization.reason ?? 'Hidden from RCM morning worklist'}
                      </Typography>
                    </div>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        unhideSelectedOrganization(
                          organization.organization_id,
                          organization.organization_name,
                        )
                      }
                      disabled={isUnhidingOrganization}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeHiddenModal}>Close</Button>
          </DialogActions>
        </Dialog>
      </main>
    </div>
  );
}
